import React, { useState } from 'react'
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom'
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Badge,
  Avatar,
  Tooltip,
} from '@mui/material'
import { Logout, Notifications, Person, Assignment, Event, Description, Menu as MenuIcon } from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { logout } from '../store/slices/authSlice'
import { getMenuItemsForRole } from '../config/menu'
import type { MenuItem as MenuItemType } from '../config/menu'
import Logo from './Logo'
import AppFooter from './AppFooter'
import LitigantChatbot from './LitigantChatbot'

const drawerWidth = 220

/** Topbar – navy #1A2B4A */
const TOPBAR_NAVY = '#1A2B4A'

/** Sidebar – navy đậm #162238, active #2A4A72, border vàng #F5C518, hover #1E3555 */
const SIDEBAR_NAVY = '#162238'
const SIDEBAR_ACTIVE = '#2A4A72'
const SIDEBAR_HOVER = '#1E3555'
const SIDEBAR_BORDER_ACTIVE = '#F5C518'
const SIDEBAR_TEXT = 'rgba(255, 255, 255, 0.95)'
const SIDEBAR_DIVIDER = 'rgba(255, 255, 255, 0.15)'

const MODULE_NAMES: Record<string, string> = {
  '/workspace': 'Bảng điều khiển',
  '/workspace/cases': 'Danh sách án',
  '/workspace/calendar': 'Lịch làm việc',
  '/workspace/reports': 'Báo cáo',
  '/workspace/preliminary': 'Xử lý sơ bộ',
  '/workspace/preliminary-chief': 'Quyết định thẩm quyền',
  '/workspace/documents-pending': 'Văn bản chờ duyệt / Ký',
  '/workspace/assign': 'Phân công án',
  '/workspace/settings': 'Cài đặt hệ thống',
  '/workspace/video': 'Phiên tòa trực tuyến',
  '/workspace/ai-draft': 'AI Soạn thảo văn bản',
  '/workspace/ai-evidence': 'AI Chứng cứ',
  '/workspace/ai-research': 'AI Research',
  '/cases': 'Vụ án của tôi',
  '/profile': 'Hồ sơ cá nhân',
}

function getModuleName(pathname: string): string {
  const base = pathname.split('?')[0]
  if (MODULE_NAMES[base]) return MODULE_NAMES[base]
  if (base.startsWith('/workspace/assign/')) return 'Chọn Thẩm phán'
  if (base.startsWith('/workspace/cases/')) return 'Chi tiết vụ án'
  if (base.startsWith('/cases/')) return 'Chi tiết vụ án'
  return 'UEL E-Court'
}

const roleLabels: Record<string, string> = {
  user: 'Đương sự',
  student: 'Sinh viên',
  litigant: 'Đương sự',
  secretary: 'Thư ký',
  judge: 'Thẩm phán',
  admin: 'Chánh án',
  chief_judge: 'Chánh án',
  support: 'Hỗ trợ',
  coach: 'Huấn luyện viên',
}

/** Trả về path của mục menu đang được chọn (mục có path khớp dài nhất với pathname). */
function getSelectedMenuPath(pathname: string, items: { path: string; isAIGroup?: boolean }[]): string {
  const matching = items.filter(
    (item) =>
      pathname === item.path ||
      (item.path !== '/' && pathname.startsWith(item.path + '/')) ||
      (item.isAIGroup && pathname.startsWith('/workspace/ai-'))
  )
  if (matching.length === 0) return ''
  const best = matching.sort((a, b) => b.path.length - a.path.length)[0]
  return best.path
}

/** Lấy tab từ search (vd. ?tab=new) – dùng để highlight sub-menu. */
function getPreliminaryTab(search: string): string {
  const params = new URLSearchParams(search)
  const tab = params.get('tab') ?? 'new'
  return tab
}

export default function Layout() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { isAuthenticated, user } = useAppSelector((state) => state.auth)
  const menuItems = getMenuItemsForRole(user?.role ?? 'user')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const moduleName = getModuleName(location.pathname)

  const mockNotifications = [
    { id: '1', title: 'Văn bản chờ duyệt', body: '3 văn bản cần Thẩm phán duyệt.', time: '10 phút trước', icon: Description, path: '/workspace/documents-pending', unread: true },
    { id: '2', title: 'Phiên họp sắp diễn ra', body: 'HN-GD-2026-001 – Phiên kiểm tra chứng cứ 25/03/2026.', time: '1 giờ trước', icon: Event, path: '/workspace/calendar', unread: true },
    { id: '3', title: 'Đơn mới tiếp nhận', body: 'HN-GD-2026-006 chờ xử lý sơ bộ.', time: '2 giờ trước', icon: Assignment, path: '/workspace/preliminary', unread: false },
  ]
  const notifCount = mockNotifications.filter((n) => n.unread).length
  const isStaff = ['secretary', 'judge', 'admin', 'chief_judge'].includes(user?.role ?? '')

  const handleLogout = () => {
    setAnchorEl(null)
    dispatch(logout())
    navigate('/')
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100vh', maxHeight: '100vh', overflow: 'hidden' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: TOPBAR_NAVY,
          color: '#fff',
        }}
      >
        <Toolbar sx={{ minHeight: 56 }}>
          {isMobile && (
            <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 1 }} aria-label="Mở menu">
              <MenuIcon />
            </IconButton>
          )}
          <Logo variant="topbar" showText={true} />
          <Typography variant="h6" sx={{ ml: 2, fontWeight: 600, fontSize: '1rem' }}>
            {moduleName}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title={notifCount ? `${notifCount} thông báo mới` : 'Thông báo'}>
            <IconButton color="inherit" size="small" sx={{ mr: 0.5 }} onClick={(e) => setNotifAnchor(e.currentTarget)} aria-label="Thông báo">
              <Badge badgeContent={notifCount || 0} color="error">
                <Notifications fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={notifAnchor}
            open={Boolean(notifAnchor)}
            onClose={() => setNotifAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { mt: 1.5, width: 360, maxWidth: '90vw', maxHeight: 400 } } }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" fontWeight={600}>Thông báo</Typography>
              {notifCount > 0 && (
                <Typography variant="caption" color="text.secondary">{notifCount} chưa đọc</Typography>
              )}
            </Box>
            <List dense sx={{ py: 0, maxHeight: 320, overflow: 'auto' }}>
              {mockNotifications.map((n) => {
                const Icon = n.icon
                return (
                  <ListItem
                    key={n.id}
                    disablePadding
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                    onClick={() => { setNotifAnchor(null); navigate(n.path) }}
                  >
                    <ListItemButton sx={{ py: 1.5, gap: 1.5 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Icon fontSize="small" color="primary" />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={n.title}
                        secondary={n.body}
                        primaryTypographyProps={{ fontWeight: n.unread ? 600 : 500, fontSize: 14 }}
                        secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary', sx: { mt: 0.25 } }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{n.time}</Typography>
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
            <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
              <MenuItem onClick={() => { setNotifAnchor(null); navigate('/workspace') }} dense>
                <ListItemText primary="Xem tất cả" primaryTypographyProps={{ variant: 'body2', color: 'primary.main' }} />
              </MenuItem>
            </Box>
          </Menu>
          <IconButton color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ ml: 0.5 }} aria-label="Tài khoản">
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.3)' }}>
              {(user?.full_name || user?.username || 'U').charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Typography variant="body2" sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }}>
            {user?.full_name || user?.username}
          </Typography>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }} slotProps={{ paper: { sx: { mt: 1.5 } } }}>
            <MenuItem onClick={() => { setAnchorEl(null); navigate('/profile') }}>
              <ListItemIcon><Person fontSize="small" /></ListItemIcon>
              Hồ sơ cá nhân
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
              Đăng xuất
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
<Box sx={{ display: 'flex', flex: 1, minHeight: 0, width: '100%', minWidth: 0, alignItems: 'stretch' }}>
        <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        ModalProps={isMobile ? { keepMounted: true } : undefined}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            bgcolor: SIDEBAR_NAVY,
            color: SIDEBAR_TEXT,
            borderRight: 'none',
          },
        }}
      >
        <Logo variant="sidebar" />
        <Divider sx={{ borderColor: SIDEBAR_DIVIDER }} />
        <List sx={{ pt: 1, px: 0 }}>
          {isStaff && (
            <>
              <ListItem sx={{ py: 0.5, px: 2 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, textTransform: 'uppercase' }}>
                  Hồ sơ cá nhân
                </Typography>
              </ListItem>
              <Divider sx={{ borderColor: SIDEBAR_DIVIDER, my: 0.5 }} />
            </>
          )}
          {menuItems.map((item) => {
            const isSelected = getSelectedMenuPath(location.pathname, menuItems) === item.path
            const showGroupDivider = item.isAIGroup
            return (
              <React.Fragment key={item.path}>
                {showGroupDivider && <Divider sx={{ borderColor: SIDEBAR_DIVIDER, my: 1 }} />}
                <ListItem disablePadding sx={{ px: 1, mb: 0.5 }}>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false) }}
                    sx={{
                      py: 1.25,
                      px: 2,
                      borderRadius: 0,
                      color: SIDEBAR_TEXT,
                      transition: 'background-color 150ms',
                      '&:hover': { bgcolor: SIDEBAR_HOVER },
                      '&.Mui-selected': {
                        bgcolor: SIDEBAR_ACTIVE,
                        color: 'white',
                        borderLeft: 3,
                        borderColor: SIDEBAR_BORDER_ACTIVE,
                        '&:hover': { bgcolor: SIDEBAR_ACTIVE },
                      },
                      '& .MuiListItemIcon-root': { color: 'inherit', minWidth: 40 },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} primaryTypographyProps={{ variant: 'body2', fontSize: 14, fontWeight: isSelected ? 600 : 400 }} />
                  </ListItemButton>
                </ListItem>
                {item.children && (location.pathname.startsWith(item.path) || (item.isAIGroup && location.pathname.startsWith('/workspace/ai-'))) && (
                  <Box sx={{ mx: 0, mb: 1, py: 0.5 }}>
                    {(item as MenuItemType).children!.map((sub) => {
                      const subPath = sub.path.split('?')[0]
                      const subTab = sub.path.includes('tab=') ? sub.path.split('tab=')[1]?.split('&')[0] ?? sub.id : sub.id
                      const isSubActive = item.isAIGroup ? location.pathname === subPath : location.pathname === subPath && getPreliminaryTab(location.search) === subTab
                      return (
                        <ListItemButton
                          key={sub.id}
                          onClick={() => { navigate(sub.path); if (isMobile) setMobileOpen(false) }}
                          sx={{
                            py: 1,
                            px: 2,
                            color: SIDEBAR_TEXT,
                            '&:hover': { bgcolor: SIDEBAR_HOVER },
                            bgcolor: isSubActive ? SIDEBAR_ACTIVE : 'transparent',
                            borderLeft: 3,
                            borderColor: isSubActive ? SIDEBAR_BORDER_ACTIVE : 'transparent',
                          }}
                        >
                          <ListItemText primary={sub.text} primaryTypographyProps={{ variant: 'body2', fontWeight: isSubActive ? 600 : 500 }} />
                        </ListItemButton>
                      )
                    })}
                  </Box>
                )}
              </React.Fragment>
            )
          })}
        </List>
        {user && (
          <>
            <Divider sx={{ borderColor: SIDEBAR_DIVIDER, mt: 'auto' }} />
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'rgba(255,255,255,0.2)', fontSize: 14 }}>
                {(user.full_name || user.username || 'U').charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" noWrap sx={{ color: SIDEBAR_TEXT, fontWeight: 500 }}>
                  {user.full_name || user.username}
                </Typography>
              </Box>
              <IconButton color="inherit" size="small" onClick={handleLogout} aria-label="Đăng xuất">
                <Logout fontSize="small" />
              </IconButton>
            </Box>
          </>
        )}
      </Drawer>
      <Box
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 0%',
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          minWidth: 0,
          minHeight: 0,
          mt: 7,
          pt: { xs: 2, sm: 3 },
          pr: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
          pl: { xs: 2, sm: 3 },
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: '1 1 0%',
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            minHeight: 0,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflow: 'auto',
            /* Page luôn full width */
            '& > *': {
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
            },
          }}
        >
          <Outlet />
        </Box>
        <Box component="footer" sx={{ flexShrink: 0 }}>
          <AppFooter />
        </Box>
      </Box>
      </Box>
      {(user?.role === 'user' || user?.role === 'litigant') && <LitigantChatbot />}
    </Box>
  )
}
